// GraphQL Query Operations with TypeScript types
import type { 
  Application, 
  ApplicationConnection,
  Application_Execs,
  Application_ExecsConnection,
  Application_Product,
  Application_ProductConnection,
  Application_Step_Status,
  Application_Step_StatusConnection,
  Config_Param,
  Config_ParamConnection,
  Data_Element,
  Data_ElementConnection
} from '../generated/graphql'

/**
 * Usage example with generated types:
 * 
 * import { executeGraphQL } from '@/utils/unifiedGraphQLClient'
 * import { listApplication } from '@/graphql/queries'
 * import type { ApplicationConnection } from '@/generated/graphql'
 * 
 * const result = await executeGraphQL<{ listAPPLICATION: ApplicationConnection }>(
 *   listApplication,
 *   { limit: 100 }
 * )
 * 
 * // result.data.listAPPLICATION is now fully typed!
 */

// List queries for basic entities
export const listApplication = `
  query ListApplication($limit: Int, $nextToken: String) {
    listAPPLICATION(limit: $limit, nextToken: $nextToken) {
      items {
        APPLICATION_ID
        ORIGIN_APPLICATION_ID
        PROCESS_FLAG
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listApplicationExecs = `
  query ListApplicationExecs($limit: Int, $nextToken: String) {
    listAPPLICATION_EXECS(limit: $limit, nextToken: $nextToken) {
      items {
        APPLICATION_EXEC_ID
        APPLICATION_ID
        AWS_EXEC_ID
        CREATED_DATE
      }
      nextToken
    }
  }
`

export const listApplicationProduct = `
  query ListApplicationProduct($limit: Int, $nextToken: String) {
    listAPPLICATION_PRODUCT(limit: $limit, nextToken: $nextToken) {
      items {
        APPLICATION_PRODUCT_ID
        APPLICATION_ID
        ORIGIN_PRODUCT_ID
        INSTANCE_NBR
        PROCESS_FLAG
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listApplicationStepStatus = `
  query ListApplicationStepStatus($limit: Int, $nextToken: String) {
    listAPPLICATION_STEP_STATUS(limit: $limit, nextToken: $nextToken) {
      items {
        APPLICATION_STEP_STATUS_ID
        APPLICATION_ID
        APPLICATION_EXEC_ID
        APPLICATION_PRODUCT_ID
        STEP_TYPE_ID
        COMPLETE_FLAG
        SEQUENCE_NBR
        IDX_JSON_PATH
        RESPONSE_TEXT
        OUTPUT_JSON
        CREATED_DATE
      }
      nextToken
    }
  }
`

export const listConfigParam = `
  query ListConfigParam($limit: Int, $nextToken: String) {
    listCONFIG_PARAM(limit: $limit, nextToken: $nextToken) {
      items {
        CONFIG_PARAM_ID
        CONFIG_NAME
        CONFIG_VALUE
        DESCRIPTION
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listDataElement = `
  query ListDataElement($limit: Int, $nextToken: String) {
    listDATA_ELEMENT(limit: $limit, nextToken: $nextToken) {
      items {
        DATA_ELEMENT_ID
        ELEMENT_KEY
        ELEMENT_VALUE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listFilterCriteria = `
  query ListFilterCriteria($limit: Int, $nextToken: String) {
    listFILTER_CRITERIA(limit: $limit, nextToken: $nextToken) {
      items {
        FILTER_CRITERIA_ID
        ORIGIN_PRODUCT_ID
        STEP_TYPE_ID
        CRITERIA
        SEQUENCE_NBR
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listLoanApp = `
  query ListLoanApp($limit: Int, $nextToken: String) {
    listLOAN_APP(limit: $limit, nextToken: $nextToken) {
      items {
        LOAN_APP_ID
        ORIGIN_LOAN_APP_ID
        ORIGIN_PRODUCT_ID
        PROCESS_FLAG
        EXEC_ID
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listLoanAppExecs = `
  query ListLoanAppExecs($limit: Int, $nextToken: String) {
    listLOAN_APP_EXECS(limit: $limit, nextToken: $nextToken) {
      items {
        LOAN_APP_EXEC_ID
        LOAN_APP_ID
        AWS_EXEC_ID
        CREATED_DATE
      }
      nextToken
    }
  }
`

export const listLoanAppStepStatus = `
  query ListLoanAppStepStatus($limit: Int, $nextToken: String) {
    listLOAN_APP_STEP_STATUS(limit: $limit, nextToken: $nextToken) {
      items {
        LOAN_APP_STEP_STATUS_ID
        LOAN_APP_ID
        LOAN_APP_EXEC_ID
        STEP_TYPE_ID
        COMPLETE_FLAG
        SEQUENCE_NBR
        IDX_JSON_PATH
        RESPONSE_TEXT
        OUTPUT_JSON
        CREATED_DATE
      }
      nextToken
    }
  }
`

export const listNewMemberToken = `
  query ListNewMemberToken($limit: Int, $nextToken: String) {
    listNEW_MEMBER_TOKEN(limit: $limit, nextToken: $nextToken) {
      items {
        NEW_MEMBER_TOKEN_ID
        MEMBER_NBR
        CARD_NBR
        PERSON_NBR
        LOAN_APP_ID
        FIRST_NAME
        LAST_NAME
        DOB
        TAX_ID
        EMAIL_ADDRESS_TEXT
        ZIP_CODE
        HOME_PHONE_NBR
        MOBILE_PHONE_NBR
        TOKEN_VALUE
        TOKEN_USED_FLAG
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listOriginProduct = `
  query ListOriginProduct($limit: Int, $nextToken: String) {
    listORIGIN_PRODUCT(limit: $limit, nextToken: $nextToken) {
      items {
        ORIGIN_PRODUCT_ID
        VENDOR_NAME
        PRODUCT_ID
        PSCU_CLIENT_ID
        PRODUCT_DESC
        PARTNER_CODE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listOriginProductComparison = `
  query ListOriginProductComparison($limit: Int, $nextToken: String) {
    listOriginProductComparison(limit: $limit, nextToken: $nextToken) {
      items {
        ORIGIN_PRODUCT_ID
        VENDOR_NAME
        PRODUCT_ID
        PSCU_CLIENT_ID
        PRODUCT_DESC
        PARTNER_CODE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listRedirectUrl = `
  query ListRedirectUrl($limit: Int, $nextToken: String) {
    listREDIRECT_URL(limit: $limit, nextToken: $nextToken) {
      items {
        REDIRECT_URL_ID
        ORIGIN_PRODUCT_ID
        URL_TYPE_CODE
        URL
        RESPONSE_TEXT
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listService = `
  query ListService($limit: Int, $nextToken: String) {
    listSERVICE(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceDomain = `
  query ListServiceDomain($limit: Int, $nextToken: String) {
    listSERVICE_DOMAIN(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_DOMAIN_ID
        DOMAIN_URL
        SERVICE_PROVIDER_ID
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceExprMapping = `
  query ListServiceExprMapping($limit: Int, $nextToken: String) {
    listSERVICE_EXPR_MAPPING(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_EXPR_MAPPING_ID
        SERVICE_PARAM_MAPPING_ID
        STEP_TYPE_ID
        SEQUENCE_NBR
        SOURCE_EXPR
        TARGET_EXPR
        COMMENT_TEXT
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceParam = `
  query ListServiceParam($serviceId: Int, $limit: Int, $nextToken: String) {
    listSERVICE_PARAM(serviceId: $serviceId, limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_PARAM_ID
        SERVICE_ID
        PARAM_NAME
        IS_SENSITIVE
        DATA_ELEMENT_ID
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceParamMapping = `
  query ListServiceParamMapping($limit: Int, $nextToken: String) {
    listSERVICE_PARAM_MAPPING(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_PARAM_MAPPING_ID
        ORIGIN_PRODUCT_ID
        SYSTEM_NBR
        PRIN_NBR
        AGENT_NBR
        SOURCE_SERVICE_PARAM_ID
        TARGET_SERVICE_PARAM_ID
        PLASTIC_TYPE_ID
        COMMENT_TEXT
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceProvider = `
  query ListServiceProvider($limit: Int, $nextToken: String) {
    listSERVICE_PROVIDER(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_PROVIDER_ID
        SERVICE_PROVIDER_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listServiceResponse = `
  query ListServiceResponse($serviceId: Int, $limit: Int, $nextToken: String) {
    listSERVICE_RESPONSE(serviceId: $serviceId, limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_RESPONSE_ID
        SERVICE_ID
        RESPONSE_PATH
        DATA_TYPE
        IS_SENSITIVE
        STATUS_CODE_START
        STATUS_CODE_END
        DATA_ELEMENT_ID
      }
      nextToken
    }
  }
`

export const listServiceResponseMapping = `
  query ListServiceResponseMapping($limit: Int, $nextToken: String) {
    listSERVICE_RESPONSE_MAPPING(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_RESPONSE_MAPPING_ID
        SERVICE_RESPONSE_ID
        ORIGIN_PRODUCT_ID
        OUTPUT_PATH
      }
      nextToken
    }
  }
`

export const listSortCriteria = `
  query ListSortCriteria($limit: Int, $nextToken: String) {
    listSORT_CRITERIA(limit: $limit, nextToken: $nextToken) {
      items {
        SORT_CRITERIA_ID
        ORIGIN_PRODUCT_ID
        STEP_TYPE_ID
        JSON_PATH
        SORT_ORDER
        SORT_PRIORITY
        SEQUENCE_NBR
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listStepServiceMapping = `
  query ListStepServiceMapping($limit: Int, $nextToken: String) {
    listSTEP_SERVICE_MAPPING(limit: $limit, nextToken: $nextToken) {
      items {
        STEP_SERVICE_MAPPING_ID
        STEP_TYPE_ID
        SERVICE_ID
        SEQUENCE_NBR
      }
      nextToken
    }
  }
`

export const listStepType = `
  query ListStepType($limit: Int, $nextToken: String) {
    listSTEP_TYPE(limit: $limit, nextToken: $nextToken) {
      items {
        STEP_TYPE_ID
        STEP_TYPE_NAME
        STEP_TYPE_DESC
        RESOURCE_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listStepTypeParamMap = `
  query ListStepTypeParamMap($limit: Int, $nextToken: String) {
    listSTEP_TYPE_PARAM_MAP(limit: $limit, nextToken: $nextToken) {
      items {
        STEP_TYPE_PARAM_MAP_ID
        STEP_TYPE_ID
        SERVICE_PARAM_MAPPING_ID
        CREATED_BY_USER_ID
        CREATED_DATE
      }
      nextToken
    }
  }
`

// Optimized queries for better performance
export const getServiceWithDetails = `
  query GetServiceWithDetails($serviceId: Int!) {
    getServiceWithDetails(serviceId: $serviceId) {
      service {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        SERVICE_PROVIDER_NAME
      }
      parameters {
        SERVICE_PARAM_ID
        PARAM_NAME
      }
      stepMappings {
        STEP_SERVICE_MAPPING_ID
        STEP_TYPE_ID
        SEQUENCE_NBR
        STEP_TYPE_NAME
      }
      sourceMappingCount
      targetMappingCount
    }
  }
`

export const getFilteredServiceParamMappings = `
  query GetFilteredServiceParamMappings(
    $productId: Int
    $sourceServiceId: Int
    $targetServiceId: Int
    $limit: Int
    $nextToken: String
  ) {
    getFilteredServiceParamMappings(
      productId: $productId
      sourceServiceId: $sourceServiceId
      targetServiceId: $targetServiceId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        SERVICE_PARAM_MAPPING_ID
        ORIGIN_PRODUCT_ID
        SOURCE_SERVICE_PARAM_ID
        TARGET_SERVICE_PARAM_ID
        SYSTEM_NBR
        PRIN_NBR
        AGENT_NBR
        PLASTIC_TYPE_ID
        COMMENT_TEXT
        CREATED_DATE
        CHANGED_DATE
        ORIGIN_PRODUCT_ID_DISPLAY
        SOURCE_SERVICE_PARAM_ID_DISPLAY
        TARGET_SERVICE_PARAM_ID_DISPLAY
        SOURCE_SERVICE_DISPLAY
        TARGET_SERVICE_DISPLAY
      }
      nextToken
    }
  }
`

export const getProductMappingsWithServices = `
  query GetProductMappingsWithServices($productId: Int!) {
    getProductMappingsWithServices(productId: $productId) {
      product {
        ORIGIN_PRODUCT_ID
        VENDOR_NAME
        PRODUCT_ID
        PSCU_CLIENT_ID
        PRODUCT_DESC
        PARTNER_CODE
      }
      mappings {
        SERVICE_PARAM_MAPPING_ID
        SOURCE_SERVICE_PARAM_ID
        TARGET_SERVICE_PARAM_ID
        COMMENT_TEXT
      }
      redirectUrls {
        REDIRECT_URL_ID
        URL_TYPE_CODE
        URL
        RESPONSE_TEXT
      }
      sourceServices {
        SERVICE_ID
        URI
        SERVICE_PROVIDER_ID
      }
      targetServices {
        SERVICE_ID
        URI
        SERVICE_PROVIDER_ID
      }
    }
  }
`

export const getFilteredServiceExprMappings = `
  query GetFilteredServiceExprMappings(
    $productId: Int
    $sourceServiceId: Int
    $targetServiceId: Int
    $stepTypeName: String
    $limit: Int
    $nextToken: String
  ) {
    getFilteredServiceExprMappings(
      productId: $productId
      sourceServiceId: $sourceServiceId
      targetServiceId: $targetServiceId
      stepTypeName: $stepTypeName
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        SERVICE_EXPR_MAPPING_ID
        SERVICE_PARAM_MAPPING_ID
        STEP_TYPE_ID
        STEP_TYPE_NAME
        SEQUENCE_NBR
        SOURCE_EXPR
        TARGET_EXPR
        COMMENT_TEXT
        CREATED_DATE
        CHANGED_DATE
        SERVICE_PARAM_MAPPING_DISPLAY
        ORIGIN_PRODUCT_DISPLAY
        SOURCE_SERVICE_DISPLAY
        TARGET_SERVICE_DISPLAY
      }
      nextToken
    }
  }
`

export const getFilteredServiceResponseMappings = `
  query GetFilteredServiceResponseMappings(
    $productId: Int
    $serviceId: Int
    $serviceResponseId: Int
    $limit: Int
    $nextToken: String
  ) {
    getFilteredServiceResponseMappings(
      productId: $productId
      serviceId: $serviceId
      serviceResponseId: $serviceResponseId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        SERVICE_RESPONSE_MAPPING_ID
        SERVICE_RESPONSE_ID
        ORIGIN_PRODUCT_ID
        OUTPUT_PATH
        SERVICE_RESPONSE_DISPLAY
        ORIGIN_PRODUCT_DISPLAY
        SERVICE_DISPLAY
      }
      nextToken
    }
  }
`

export const getServiceParamWithDetails = `
  query GetServiceParamWithDetails($serviceParamId: Int!) {
    getServiceParamWithDetails(serviceParamId: $serviceParamId) {
      serviceParam {
        SERVICE_PARAM_ID
        SERVICE_ID
        PARAM_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      service {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      mappingCount
    }
  }
`

export const getStepTypeWithMappings = `
  query GetStepTypeWithMappings($stepTypeId: Int!) {
    getStepTypeWithMappings(stepTypeId: $stepTypeId) {
      stepType {
        STEP_TYPE_ID
        STEP_TYPE_NAME
        STEP_TYPE_DESC
        RESOURCE_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      mappings {
        STEP_SERVICE_MAPPING_ID
        STEP_TYPE_ID
        SERVICE_ID
        SEQUENCE_NBR
      }
      services {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

export const getRedirectUrlWithProduct = `
  query GetRedirectUrlWithProduct($redirectUrlId: Int!) {
    getRedirectUrlWithProduct(redirectUrlId: $redirectUrlId) {
      redirectUrl {
        REDIRECT_URL_ID
        ORIGIN_PRODUCT_ID
        URL_TYPE_CODE
        URL
        RESPONSE_TEXT
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      product {
        ORIGIN_PRODUCT_ID
        VENDOR_NAME
        PRODUCT_ID
        PSCU_CLIENT_ID
        PRODUCT_DESC
        PARTNER_CODE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

export const getServiceDomainWithProvider = `
  query GetServiceDomainWithProvider($serviceDomainId: Int!) {
    getServiceDomainWithProvider(serviceDomainId: $serviceDomainId) {
      serviceDomain {
        SERVICE_DOMAIN_ID
        DOMAIN_URL
        SERVICE_PROVIDER_ID
        CREATED_DATE
        CHANGED_DATE
      }
      provider {
        SERVICE_PROVIDER_ID
        SERVICE_PROVIDER_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

export const getStepServiceMappingWithDetails = `
  query GetStepServiceMappingWithDetails($stepServiceMappingId: Int!) {
    getStepServiceMappingWithDetails(stepServiceMappingId: $stepServiceMappingId) {
      mapping {
        STEP_SERVICE_MAPPING_ID
        STEP_TYPE_ID
        SERVICE_ID
        SEQUENCE_NBR
      }
      stepType {
        STEP_TYPE_ID
        STEP_TYPE_NAME
        STEP_TYPE_DESC
        RESOURCE_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
      service {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

export const getServiceResponseWithDetails = `
  query GetServiceResponseWithDetails($serviceResponseId: Int!) {
    getServiceResponseWithDetails(serviceResponseId: $serviceResponseId) {
      serviceResponse {
        SERVICE_RESPONSE_ID
        SERVICE_ID
        RESPONSE_PATH
        DATA_TYPE
        IS_SENSITIVE
        STATUS_CODE_START
        STATUS_CODE_END
        DATA_ELEMENT_ID
      }
      service {
        SERVICE_ID
        SERVICE_PROVIDER_ID
        URI
        SECRET_NAME
        REQUEST_TYPE
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

export const getApplicationStepStatusWithDetails = `
  query GetApplicationStepStatusWithDetails($applicationStepStatusId: Int!) {
    getApplicationStepStatusWithDetails(applicationStepStatusId: $applicationStepStatusId) {
      applicationStepStatus {
        APPLICATION_STEP_STATUS_ID
        APPLICATION_ID
        APPLICATION_EXEC_ID
        APPLICATION_PRODUCT_ID
        STEP_TYPE_ID
        COMPLETE_FLAG
        SEQUENCE_NBR
        IDX_JSON_PATH
        RESPONSE_TEXT
        OUTPUT_JSON
        CREATED_DATE
      }
      application {
        APPLICATION_ID
        ORIGIN_APPLICATION_ID
        PROCESS_FLAG
        CREATED_DATE
        CHANGED_DATE
      }
      stepType {
        STEP_TYPE_ID
        STEP_TYPE_NAME
        STEP_TYPE_DESC
        RESOURCE_NAME
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
      }
    }
  }
`

// List queries with joins for enhanced display
export const listServiceDomainsWithProvider = `
  query ListServiceDomainsWithProvider($limit: Int, $nextToken: String) {
    listServiceDomainsWithProvider(limit: $limit, nextToken: $nextToken) {
      items {
        SERVICE_DOMAIN_ID
        DOMAIN_URL
        SERVICE_PROVIDER_ID
        SERVICE_PROVIDER_NAME
        CREATED_DATE
        CHANGED_DATE
      }
      nextToken
    }
  }
`

export const listRedirectUrlsWithProduct = `
  query ListRedirectUrlsWithProduct($limit: Int, $nextToken: String) {
    listRedirectUrlsWithProduct(limit: $limit, nextToken: $nextToken) {
      items {
        REDIRECT_URL_ID
        ORIGIN_PRODUCT_ID
        URL_TYPE_CODE
        URL
        RESPONSE_TEXT
        CREATED_BY_USER_ID
        CREATED_DATE
        CHANGED_BY_USER_ID
        CHANGED_DATE
        PRODUCT_ID
        VENDOR_NAME
        PSCU_CLIENT_ID
      }
      nextToken
    }
  }
`

export const getFilteredStepTypeParamMaps = `
  query GetFilteredStepTypeParamMaps(
    $stepTypeId: Int
    $sourceServiceId: Int
    $targetServiceId: Int
    $limit: Int
    $nextToken: String
  ) {
    getFilteredStepTypeParamMaps(
      stepTypeId: $stepTypeId
      sourceServiceId: $sourceServiceId
      targetServiceId: $targetServiceId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        STEP_TYPE_PARAM_MAP_ID
        STEP_TYPE_ID
        SERVICE_PARAM_MAPPING_ID
        CREATED_BY_USER_ID
        CREATED_DATE
        STEP_TYPE_NAME
        ORIGIN_PRODUCT_ID
        PRODUCT_ID
        SOURCE_SERVICE_ID
        TARGET_SERVICE_ID
        SOURCE_PARAM_NAME
        TARGET_PARAM_NAME
        SOURCE_SERVICE_URI
        TARGET_SERVICE_URI
      }
      nextToken
    }
  }
`
